import { api } from ".";

/**
 * EKİP ve ROTA (plan 2.33/2.34).
 *
 * `shiftDefinitionApi.tsx` ile ilişkisi: orada vardiya TANIMI ("08:00–16:00
 * gündüz") ve doğrudan atama var; burada ekip ("A Ekibi") ve o ekiplerin
 * dönüşümlü ÇEVRİMİ. Rotanın kurulmasıyla "kim hangi gün hangi vardiyada"
 * sorusu elle girilen bir şey olmaktan çıkıp HESAPLANAN bir şeye dönüşüyor.
 *
 * ⚠ Hesap PANELDE DEĞİL, backend'de (21 Ağu kararı). Bu modül matris üretmez,
 * `/preview` ucundan hazır gelen matrisi taşır. Modulo/takvim-ayı matematiğini
 * burada ikinci kez yazmak, biri düzeltilip diğeri unutulduğunda ekranı
 * puantajdan sessizce ayırırdı.
 */

const teamPath = "/shift-team";
const rotationPath = "/shift-rotation";

export type ShiftTeam = {
  _id: string;
  name: string;
  code: string;
  departmentId: string;
  department?: { name: string };
  color?: string;
  notes?: string;
  /** Bugün bu ekipte olan personel sayısı — backend sayıyor. */
  memberCount?: number;
};

export type RotationPeriodType = "week" | "month";

export type ShiftRotation = {
  _id: string;
  name: string;
  departmentId: string;
  department?: { name: string };
  teamIds: string[];
  /** `null` = o dönem o ekip DİNLENİR. */
  shiftDefinitionIds: (string | null)[];
  periodType: RotationPeriodType;
  periodCount: number;
  anchorDate: string;
  startDate: string;
  endDate?: string | null;
  notes?: string;
};

/** Önizleme matrisinin tek hücresi. `shiftDefinitionId: null` = dinlenme. */
export type RotationCell = {
  teamId: string;
  teamCode: string;
  teamName: string;
  teamColor: string | null;
  shiftDefinitionId: string | null;
  shiftCode: string | null;
  shiftName: string | null;
  shiftColor: string | null;
};

export type RotationPeriod = {
  index: number;
  startDate: string;
  endDate: string;
  cells: RotationCell[];
};

export type RotationPreview = {
  rotation: {
    _id: string;
    name: string;
    periodType: RotationPeriodType;
    periodCount: number;
    anchorDate: string;
    startDate: string;
    endDate: string | null;
  };
  periods: RotationPeriod[];
};

/** Tarihleri çakışan rota — backend 409 ile bu listeyi döndürür. */
export type RotationConflict = {
  rotationId: string;
  name: string;
  startDate: string;
  endDate: string | null;
};

const cleanParams = (params: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== "")
  );

// ── Ekip ──────────────────────────────────────────────────────────────────

export const getShiftTeams = async (params: Record<string, any> = {}) => {
  const res = await api.get(`${teamPath}/list`, { params: cleanParams(params) });
  return res.data;
};

/** Rota formu ve atama ekranının "ekip" kipi için `{value,label}` listesi. */
export const getShiftTeamSelectData = async (departmentId?: string) => {
  const res = await api.get(`${teamPath}/select-data`, {
    params: cleanParams({ departmentId }),
  });
  return res.data;
};

/** Ekibin VERİLEN TARİHTEKİ üyeleri. Üyelik tarih aralıklı, o yüzden tarihsiz sorulamaz. */
export type ShiftTeamMember = {
  assignmentId: string;
  employeeId: string;
  name: string;
  startDate: string;
  endDate: string | null;
};

export const getShiftTeamMembers = async (id: string, onDate?: string) => {
  const res = await api.get(`${teamPath}/members`, {
    params: cleanParams({ _id: id, onDate }),
  });
  return res.data;
};

export const createShiftTeam = async (values: Partial<ShiftTeam>) => {
  try {
    const res = await api.post(teamPath, values);
    return res.data;
  } catch (error: any) {
    return error?.response?.data ?? { success: false };
  }
};

export const updateShiftTeam = async (id: string, values: Partial<ShiftTeam>) => {
  try {
    const res = await api.put(teamPath, { _id: id, ...values });
    return res.data;
  } catch (error: any) {
    return error?.response?.data ?? { success: false };
  }
};

export const deleteShiftTeam = async (id: string) => {
  try {
    const res = await api.delete(teamPath, { data: { _id: id } });
    return res.data;
  } catch (error: any) {
    return error?.response?.data ?? { success: false };
  }
};

// ── Rota ──────────────────────────────────────────────────────────────────

export const getShiftRotations = async (params: Record<string, any> = {}) => {
  const res = await api.get(`${rotationPath}/list`, {
    params: cleanParams(params),
  });
  return res.data;
};

export const getShiftRotationById = async (id: string) => {
  const res = await api.get(rotationPath, { params: { _id: id } });
  return res.data;
};

/**
 * "Bu hafta kim nerede" matrisi.
 *
 * `periods` backend'de 26 ile sınırlanıyor; daha fazlası istenirse kırpılır.
 */
export const getRotationPreview = async (
  id: string,
  from?: string,
  periods?: number
) => {
  const res = await api.get(`${rotationPath}/preview`, {
    params: cleanParams({ _id: id, from, periods }),
  });
  return res.data;
};

/**
 * Kaydedilmemiş bir çevrimin matrisi — rota kurma formunun canlı önizlemesi.
 *
 * ⚠ Bu uç, kaydedilmiş `/preview` ile BİREBİR aynı matrisi üretir (backend'de
 * ortak gövde, testiyle sabitlenmiş). Panelde ayrı bir taslak matematiği
 * yazmak, İK'nın kaydetmeden önce gördüğü tabloyu kaydettikten sonrakinden
 * farklı yapardı — üstelik yürürlüğe girmiş rota silinemiyor, yani yanlış
 * kaydın bedeli yüksek.
 *
 * Aynı doğrulamalardan geçer: bozuk çevrim tablo üretmez, hata döner.
 */
export const previewDraftRotation = async (payload: {
  departmentId: string;
  teamIds: string[];
  shiftDefinitionIds: (string | null)[];
  periodType: RotationPeriodType;
  periodCount: number;
  anchorDate: string;
  startDate?: string;
  endDate?: string | null;
  from?: string;
  periods?: number;
}) => {
  try {
    const res = await api.post(`${rotationPath}/preview-draft`, payload);
    return res.data;
  } catch (error: any) {
    return error?.response?.data ?? { success: false };
  }
};

/**
 * Rota oluşturur.
 *
 * Aynı departmanda tarihleri çakışan rota varsa backend **409** döner ve
 * `conflicts` gelir. Doğru çözüm eskisini KAPATMAK (bitiş tarihi vermek),
 * silmek değil — geçmiş puantaj değişmemeli.
 */
export const createShiftRotation = async (payload: {
  name: string;
  departmentId: string;
  teamIds: string[];
  shiftDefinitionIds: (string | null)[];
  periodType: RotationPeriodType;
  periodCount: number;
  anchorDate: string;
  startDate: string;
  endDate?: string | null;
  notes?: string;
}) => {
  try {
    const res = await api.post(rotationPath, payload);
    return res.data;
  } catch (error: any) {
    return error?.response?.data ?? { success: false };
  }
};

export const updateShiftRotation = async (
  id: string,
  updates: Partial<Omit<ShiftRotation, "_id" | "departmentId">>
) => {
  try {
    const res = await api.put(rotationPath, { _id: id, ...updates });
    return res.data;
  } catch (error: any) {
    return error?.response?.data ?? { success: false };
  }
};

/**
 * Rotayı siler.
 *
 * ⚠ Yalnızca HENÜZ BAŞLAMAMIŞ rota silinebilir; yürürlüğe girmiş olanı backend
 * reddeder ve kapatmaya yönlendirir. Silmek o ekipteki herkesin geçmiş
 * günlerini ölçütsüz bırakırdı.
 */
export const deleteShiftRotation = async (id: string) => {
  try {
    const res = await api.delete(rotationPath, { data: { _id: id } });
    return res.data;
  } catch (error: any) {
    return error?.response?.data ?? { success: false };
  }
};
